/**
 * StepSelectEmployee — employee card list with select and next button (step 2).
 *
 * Left panel: rendered by OnboardingShell.
 * Right panel: employee list + navigation.
 *
 * Employees come from the real Airtable People database (via ManagerReviewView).
 * Falls back to a loading skeleton / error state when data isn't ready.
 */
import { C, getRatingColor, getRatingLabel } from '../../../data/managerReviewData';
import type { Employee } from '../../../types/managerReviewV2';
import type { CompletedReview } from './StepComplete';
import OnboardingShell from './OnboardingShell';

interface StepSelectEmployeeProps {
  employees: Employee[];
  isLoading: boolean;
  error: string | null;
  onSelect: (emp: Employee) => void;
  onNext: () => void;
  onBack: () => void;
  selected: Employee | null;
  hasDraft: boolean;
  completedReviews: CompletedReview[];
  onViewAll: () => void;
  onViewIntro: () => void;
  onRevisitPrep?: () => void;
}

export default function StepSelectEmployee({
  employees,
  isLoading,
  error,
  onSelect,
  onNext,
  onBack,
  selected,
  hasDraft,
  completedReviews,
  onViewAll,
  onViewIntro,
  onRevisitPrep,
}: StepSelectEmployeeProps) {
  return (
    <OnboardingShell leftTitle="Who are we reviewing today?">

      {/* Button row — pinned to top, right-aligned */}
      <div style={{
        display: 'flex', justifyContent: 'flex-end', gap: 8,
        marginBottom: 16, paddingBottom: 14, borderBottom: `1px solid ${C.border}`,
      }}>
        <button
          onClick={onViewIntro}
          style={{
            padding: '6px 12px', borderRadius: 7,
            border: `1.5px solid ${C.border}`, background: 'transparent',
            color: C.muted, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'Inter', whiteSpace: 'nowrap',
          }}
        >📖 View intro</button>
        {completedReviews.length > 0 && (
          <button
            onClick={onViewAll}
            style={{
              padding: '6px 12px', borderRadius: 7,
              border: `1.5px solid ${C.blue}`, background: C.lightBlue,
              color: C.blue, fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'Inter', whiteSpace: 'nowrap',
            }}
          >📊 All reviews ({completedReviews.length})</button>
        )}
        {hasDraft && onRevisitPrep && (
          <button
            onClick={selected ? onRevisitPrep : undefined}
            title={!selected ? 'Select an employee first' : undefined}
            style={{
              padding: '6px 12px', borderRadius: 7,
              border: `1.5px solid ${C.border}`, background: 'transparent',
              color: C.muted, fontSize: 12, fontWeight: 600,
              cursor: selected ? 'pointer' : 'not-allowed',
              fontFamily: 'Inter', whiteSpace: 'nowrap',
              opacity: selected ? 1 : 0.45,
            }}
          >↺ Revisit preparation steps</button>
        )}
      </div>

      {/* Description */}
      <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.6, margin: '0 0 24px' }}>
        Select a direct report to get started. Each review takes around 15–20 minutes. Your reviews will be auto-saved.
      </p>

      {/* Loading state */}
      {isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{
              padding: '14px 18px', border: `1.5px solid ${C.border}`, borderRadius: 10,
              background: '#f5f5f5', height: 68,
              animation: 'pulse 1.5s ease-in-out infinite',
            }} />
          ))}
          <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.45} }`}</style>
        </div>
      )}

      {/* Error state */}
      {!isLoading && error && (
        <div style={{
          padding: '16px 18px', borderRadius: 10,
          border: `1.5px solid #fca5a5`, background: '#fff5f5',
          marginBottom: 28,
        }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: C.red, marginBottom: 4 }}>
            Could not load direct reports
          </div>
          <div style={{ fontSize: 13, color: '#444', lineHeight: 1.6 }}>{error}</div>
        </div>
      )}

      {/* Empty state — manager exists but no reports in this cycle */}
      {!isLoading && !error && employees.length === 0 && (
        <div style={{
          padding: '20px 18px', borderRadius: 10,
          border: `1.5px solid ${C.border}`, background: '#f9fafb',
          marginBottom: 28, textAlign: 'center',
        }}>
          <div style={{ fontSize: 24, marginBottom: 10 }}>🗓️</div>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 6 }}>
            No direct reports in this review cycle
          </div>
          <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
            None of your direct reports are currently in scope for this performance review cycle.
            This may be because they joined after 1 June 2026 or are at IC0 level.
            If you think this is wrong, check with your People &amp; Organization Partner.
          </div>
        </div>
      )}

      {/* Employee list */}
      {!isLoading && !error && employees.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
          {employees.map(emp => {
            const done = completedReviews.find(r => r.employee.id === emp.id);
            const isSelected = selected?.id === emp.id;
            const onLeave = emp.isOnLeave === true;

            return (
              <div
                key={emp.id}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                onClick={() => onSelect(emp)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(emp); } }}
                style={{
                  padding: '14px 18px',
                  border: `2px solid ${isSelected ? C.blue : done ? C.green : C.border}`,
                  borderRadius: 10, cursor: 'pointer',
                  background: isSelected ? C.lightBlue : done ? 'rgba(38,189,89,0.04)' : '#fff',
                  transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 14,
                  opacity: onLeave ? 0.6 : 1,
                }}
              >
                {/* Avatar */}
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: done ? C.green : onLeave ? '#999' : '#1a1a1a', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 13, flexShrink: 0,
                }}>
                  {done ? '✓' : emp.avatar}
                </div>

                {/* Name + subtitle */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Row 1: name + rating chip */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 3 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: onLeave ? '#666' : '#0f0f0f' }}>{emp.name}</span>
                    {done?.overallRating && (
                      <span style={{
                        padding: '3px 9px', borderRadius: 6,
                        background: getRatingColor(done.overallRating) + '18',
                        border: `1.5px solid ${getRatingColor(done.overallRating)}`,
                        color: getRatingColor(done.overallRating), fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                      }}>
                        {done.overallRating} – {getRatingLabel(done.overallRating)}
                      </span>
                    )}
                  </div>
                  {/* Row 2: title · level + badges */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: C.muted }}>
                      {emp.title || emp.role}{emp.level ? ` · ${emp.level}` : ''}
                    </span>
                    {onLeave && (
                      <span style={{
                        fontSize: 10, fontWeight: 700, color: '#7a5800',
                        background: 'rgba(255,180,0,0.12)', border: '1px solid rgba(200,140,0,0.35)',
                        borderRadius: 4, padding: '1px 6px', letterSpacing: '0.04em', textTransform: 'uppercase',
                      }}>On leave</span>
                    )}
                    {done?.overallRating ? (
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        color: getRatingColor(done.overallRating),
                        background: getRatingColor(done.overallRating) + '18',
                        border: `1px solid ${getRatingColor(done.overallRating)}55`,
                        borderRadius: 4, padding: '1px 6px',
                      }}>{done.overallRating} – {getRatingLabel(done.overallRating)}</span>
                    ) : emp.isFirstCycle && !onLeave ? (
                      <span style={{
                        fontSize: 10, fontWeight: 700, color: '#0060b0',
                        background: 'rgba(0,135,235,0.08)', border: '1px solid rgba(0,135,235,0.25)',
                        borderRadius: 4, padding: '1px 6px', letterSpacing: '0.04em', textTransform: 'uppercase',
                      }}>1st performance review cycle</span>
                    ) : emp.isFirstCycle ? null : (
                      <>
                        {emp.previouslyPromoted && (
                          <span style={{
                            fontSize: 10, fontWeight: 700, color: '#1a7a38',
                            background: 'rgba(38,189,89,0.1)', border: '1px solid rgba(38,189,89,0.3)',
                            borderRadius: 4, padding: '1px 6px', letterSpacing: '0.04em', textTransform: 'uppercase',
                          }}>Promoted last cycle</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12 }}>
        <button
          onClick={onBack}
          style={{
            fontFamily: 'Inter', fontWeight: 600, fontSize: 14,
            border: '1.5px solid #e5e7eb', cursor: 'pointer', borderRadius: 8,
            padding: '12px 20px', background: 'transparent', color: '#1a1a1a',
          }}
        >← Back</button>
        <button
          onClick={onNext}
          disabled={!selected}
          style={{
            fontFamily: 'Inter', fontWeight: 700, fontSize: 15,
            border: 'none', cursor: selected ? 'pointer' : 'not-allowed', borderRadius: 8,
            padding: '14px 20px', background: C.blue, color: '#fff',
            flex: 1, textAlign: 'center', opacity: selected ? 1 : 0.45,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {(() => {
            const firstName = selected?.name?.split(' ')[0] || '...';
            const isCompleted = selected && completedReviews.some(r => r.employee.id === selected.id);
            if (isCompleted) return `Edit ${firstName}'s review`;
            if (hasDraft && selected) return `Continue reviewing ${firstName}`;
            return `Review ${firstName} →`;
          })()}
        </button>
      </div>

    </OnboardingShell>
  );
}
